import { KeyboardEvent, useRef, useState } from 'react';
import { Keyboard, Package } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { getProductsByCaseId } from 'services/api/palletBuild';
import { playErrorSound, playGoodSound } from 'utils/sounds';

export const CaseScanPage = () => {
  const [scanValue, setScanValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const { setCaseId, setCaseProducts, caseProducts } = usePalletBuildStore();

  const handleSubmit = async () => {
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setHasError(false);
    toast('Scanning case...');
    try {
      const data = await getProductsByCaseId(trimmed);
      if (!data.success) {
        setErrorMessage('Failed to scan case. Please try again.');
        setHasError(true);
        playErrorSound();
        return;
      }
      if (data.data.cartonExist) {
        setCaseId(trimmed, false);
        setCaseProducts(data.data.result);
        toast.success('Case already exists. Loading products...');
      } else {
        setCaseId(trimmed, true);
        toast.success('New case — add products to build it');
      }
      playGoodSound();
      setScanValue('');
      setCurrentScreen(SCREEN_TYPES.PALLET_BUILD_ASSIGN_PRODUCTS);
    } catch {
      setErrorMessage('Failed to scan case. Please try again.');
      setHasError(true);
      playErrorSound();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onCancel = () => {
    setScanValue('');
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        hasError && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${DISPOSITION_COLORS.PROBLEM.gradient}`
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError ? 'text-white/80' : 'text-emerald-500'
          )}
        >
          Pallet Build · Step 1
        </span>
        <h2 className={cn('text-2xl font-bold', hasError ? 'text-white' : 'text-slate-800')}>
          Scan Case
        </h2>
        {hasError && <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>}
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
          <Package className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            Create or select a case
          </span>
          <span className="text-sm text-slate-400">Scan a case ID to begin assigning products</span>
        </div>
      </div>

      {caseProducts.length > 0 && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-2.5">
          <p className="text-xs text-emerald-600">
            Current case has <span className="font-bold">{caseProducts.length}</span> product
            {caseProducts.length !== 1 ? 's' : ''} — scanning a new case will switch context.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Case Barcode</Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Scan or type case ID..."
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container !bg-transparent">
        <Button onClick={onCancel} variant="secondary" className="flex-1 h-12">
          Back
        </Button>
      </div>
    </div>
  );
};
